import { Box, Button, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { X } from 'lucide-react';
import { FunctionComponent, MouseEvent } from 'react';

interface NotificationListItemProps {
  id: string;
  title: string;
  timeAgo: string;
  unread: boolean;
  /** The call to action renders only when both `ctaLabel` and `onCtaClick` are given. */
  ctaLabel?: string;
  dismissLabel: string;
  onCtaClick?: () => void;
  onDismiss: () => void;
}

export const NotificationListItem: FunctionComponent<NotificationListItemProps> = ({
  id,
  title,
  timeAgo,
  unread,
  ctaLabel,
  dismissLabel,
  onCtaClick,
  onDismiss,
}) => {
  const handleDismiss = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onDismiss();
  };

  return (
    <Box
      component="li"
      sx={{
        listStyle: 'none',
        px: 2,
        py: 1.5,
        borderBottom: 1,
        borderColor: 'divider',
        '&:last-of-type': {
          borderBottom: 0,
        },
        '& .notification-dismiss': {
          opacity: 0,
        },
        '&:hover .notification-dismiss, &:focus-within .notification-dismiss': {
          opacity: 1,
        },
      }}
      data-testid={`header-notifications-item-${id}`}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" columnGap={1}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1, minWidth: 0 }}>
          {title}
        </Typography>
        <Tooltip title={dismissLabel}>
          <IconButton
            className="notification-dismiss"
            size="small"
            aria-label={dismissLabel}
            onClick={handleDismiss}
            data-testid={`header-notifications-dismiss-${id}`}
            sx={{ flexShrink: 0, mt: -0.5, mr: -0.5 }}
          >
            <X size={16} />
          </IconButton>
        </Tooltip>
      </Stack>
      <Stack direction="row" alignItems="center" justifyContent="space-between" columnGap={1} sx={{ mt: 1 }}>
        {ctaLabel && onCtaClick ? (
          <Button size="small" variant="outlined" onClick={onCtaClick} data-testid={`header-notifications-cta-${id}`}>
            {ctaLabel}
          </Button>
        ) : null}
        <Stack direction="row" alignItems="center" columnGap={0.75} sx={{ flexShrink: 0, ml: 'auto' }}>
          <Typography variant="caption" color="text.secondary">
            {timeAgo}
          </Typography>
          {unread ? (
            <Box
              aria-hidden
              data-testid={`header-notifications-unread-dot-${id}`}
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: 'primary.main',
              }}
            />
          ) : null}
        </Stack>
      </Stack>
    </Box>
  );
};
